import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useLocation, Link } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaClock, FaSpinner } from 'react-icons/fa';
import { invitationApi } from '../../api/guestList/invitationApi';
import { toast } from 'react-toastify';
import { handleApiResponse } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import harusiLogo from './logo1.png';
import './PublicRsvp.css';

const PublicRsvp = () => {
  const { guestId: paramGuestId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Extract guest ID & event ID & name from params or query string
  const queryGuestId = searchParams.get('guest') || searchParams.get('guest_id') || searchParams.get('id');
  const queryEventId = searchParams.get('event_id') || searchParams.get('event');
  const queryGuestName = searchParams.get('name') || searchParams.get('guest_name') || '';
  const initialGuestId = paramGuestId || queryGuestId || '';

  const [guestId, setGuestId] = useState(initialGuestId);
  const [eventId, setEventId] = useState(queryEventId || null);
  const [guestName, setGuestName] = useState(queryGuestName);
  const [eventTitle, setEventTitle] = useState('');
  const [selectedRsvp, setSelectedRsvp] = useState('1'); // default to 1 (Nitahudhuria)
  const [loading, setLoading] = useState(false);
  const [fetchingInfo, setFetchingInfo] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedStatus, setSubmittedStatus] = useState(null);

  useEffect(() => {
    document.title = 'RSVP | Harusi Online';
  }, []);

  useEffect(() => {
    if (initialGuestId) {
      setGuestId(initialGuestId);
      fetchGuestDetails(initialGuestId);
    }
  }, [initialGuestId]);

  const fetchGuestDetails = async (idToFetch) => {
    if (!idToFetch) return;
    setFetchingInfo(true);
    try {
      const activeEvtId = eventId || queryEventId || null;
      const response = await invitationApi.getInvitationRsvp(idToFetch, {
        ...(activeEvtId ? { event_id: Number(activeEvtId) || activeEvtId } : {}),
        guest_id: String(idToFetch)
      });
      const rawData = response?.data || response;
      const dataPayload = rawData?.data || rawData;
      const guestData = dataPayload?.guest || dataPayload;
      const eventData = dataPayload?.event || rawData?.event || {};

      const guestNameFound = guestData?.name || guestData?.guest_name || guestData?.full_name || dataPayload?.guest_name || rawData?.name || '';
      if (guestNameFound) {
        setGuestName(guestNameFound);
      }

      const eventIdFound = guestData?.event_id || rawData?.event_id || eventData?.id || eventData?.event_id || activeEvtId;
      if (eventIdFound) {
        setEventId(eventIdFound);
      }

      const guestIdFound = guestData?.id || rawData?.guest_id || idToFetch;
      if (guestIdFound) {
        setGuestId(String(guestIdFound));
      }

      const rsvpFound = guestData?.rsvp ?? rawData?.rsvp;
      if (rsvpFound !== undefined && rsvpFound !== null && String(rsvpFound) !== '0') {
        setSelectedRsvp(String(rsvpFound));
      }

      if (eventData?.event_title) {
        setEventTitle(eventData.event_title);
      }
    } catch (err) {
      // Non-blocking if GET /api/invitation-rsvp/{guestId} is unavailable or protected
      console.log('RSVP guest fetch notice:', err);
    } finally {
      setFetchingInfo(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!guestId) {
      toast.error('Namba ya utambulisho wa mgeni (Guest ID) inahitajika.');
      return;
    }

    setLoading(true);
    try {
      const activeEventId = eventId || queryEventId || null;
      const payload = {
        ...(activeEventId ? { event_id: Number(activeEventId) || activeEventId } : {}),
        guest_id: String(guestId),
        rsvp: Number(selectedRsvp) || selectedRsvp,
        submit: 'Hakiki'
      };

      const response = await invitationApi.recordRsvp(guestId, payload);

      // Check response status / success
      if (response?.success !== false) {
        const rawData = response?.data || response;
        const dataPayload = rawData?.data || rawData;
        const guestData = dataPayload?.guest || dataPayload;
        const eventData = dataPayload?.event || {};

        if (guestData?.name) {
          setGuestName(guestData.name);
        }
        if (eventData?.event_title) {
          setEventTitle(eventData.event_title);
        }

        setSubmittedStatus(selectedRsvp);
        setSubmitted(true);
        toast.success('Ahsante! Majibu yako ya RSVP yamehifadhiwa kikamilifu.');
      } else {
        handleApiResponse(response);
      }
    } catch (err) {
      console.error('Error submitting RSVP:', err);
      handleApiResponse(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusDetails = (val) => {
    switch (String(val)) {
      case '1':
        return {
          title: 'Nitahudhuria',
          class: 'yes',
          icon: <FaCheckCircle />,
          message: 'Umethibitisha kuwa utashiriki katika tukio hili. Karibu sana!'
        };
      case '3':
        return {
          title: 'Huenda Nikahudhuria',
          class: 'maybe',
          icon: <FaClock />,
          message: 'Majibu yako yamepokelewa. Tafadhali thibitisha tena ukipata uhakika kabla ya siku ya shughuli!'
        };
      case '2':
        return {
          title: 'Sitaweza Kuhudhuria',
          class: 'no',
          icon: <FaTimesCircle />,
          message: 'Majibu yako kuwa hutaweza kuhudhuria yamepokelewa. Tunashukuru kwa kutoa taarifa mapema!'
        };
      default:
        return {
          title: 'Imethibitishwa',
          class: 'yes',
          icon: <FaCheckCircle />,
          message: 'Majibu yako ya RSVP yamepokelewa kikamilifu.'
        };
    }
  };

  return (
    <div className="rsvp-page-wrapper">
      <div className="rsvp-card-container">
        {/* Background decorative watermark */}
        <img src={harusiLogo} alt="" className="rsvp-bg-watermark" aria-hidden="true" />

        {/* Brand Logo Header */}
        <div className="rsvp-brand-header">
          <div className="rsvp-logo-wrapper">
            <img src={harusiLogo} alt="Harusi Online" className="rsvp-logo-img" />
          </div>
        </div>

        {submitted ? (
          /* Success Screen */
          <div className="rsvp-success-box">
            <div className="rsvp-success-icon-wrap">
              <FaCheckCircle />
            </div>
            <h2 className="rsvp-success-title">Ahsante Sana{guestName ? `, ${guestName}` : ''}!</h2>
            {eventTitle && (
              <p className="text-primary fw-semibold mb-2" style={{ fontSize: '0.95rem' }}>
                {eventTitle}
              </p>
            )}
            <p className="rsvp-success-desc">
              {getStatusDetails(submittedStatus).message}
            </p>

            <div className={`rsvp-badge-pill ${getStatusDetails(submittedStatus).class}`}>
              {getStatusDetails(submittedStatus).icon}
              <span>{getStatusDetails(submittedStatus).title}</span>
            </div>
          </div>
        ) : (
          /* RSVP Form Screen */
          <form onSubmit={handleSubmit} className="rsvp-form-content">
            <div className="rsvp-title-section">
              {eventTitle && (
                <div className="rsvp-event-header mb-2 text-center">
                  <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2 rounded-pill fw-semibold" style={{ fontSize: '0.88rem', whiteSpace: 'normal', lineHeight: '1.4' }}>
                    {eventTitle}
                  </span>
                </div>
              )}
              <h2 className="rsvp-main-title">Habari, Tafadhali hakiki kushiriki!</h2>
              {guestName && (
                <div className="rsvp-guest-tag">
                  <strong>{guestName}</strong>
                </div>
              )}
              <p className="rsvp-subtitle">
                Chagua moja ya machaguo yafuatayo:
              </p>
            </div>

            {/* Options Selection */}
            <div className="rsvp-options-group">
              {/* Option 1: Nitahudhuria */}
              <div
                className={`rsvp-option-card ${selectedRsvp === '1' ? 'selected-yes' : ''}`}
                onClick={() => setSelectedRsvp('1')}
              >
                <div className="rsvp-radio-circle">
                  {selectedRsvp === '1' && <div className="rsvp-radio-inner" />}
                </div>
                <span className="rsvp-option-text">Nitahudhuria</span>
                <FaCheckCircle className="rsvp-option-icon" />
              </div>

              {/* Option 3: Huenda Nikahudhuria */}
              <div
                className={`rsvp-option-card ${selectedRsvp === '3' ? 'selected-maybe' : ''}`}
                onClick={() => setSelectedRsvp('3')}
              >
                <div className="rsvp-radio-circle">
                  {selectedRsvp === '3' && <div className="rsvp-radio-inner" />}
                </div>
                <span className="rsvp-option-text">Huenda Nikahudhuria</span>
                <FaClock className="rsvp-option-icon" />
              </div>

              {/* Option 2: Sitaweza Kuhudhuria (last option) */}
              <div
                className={`rsvp-option-card ${selectedRsvp === '2' ? 'selected-no' : ''}`}
                onClick={() => setSelectedRsvp('2')}
              >
                <div className="rsvp-radio-circle">
                  {selectedRsvp === '2' && <div className="rsvp-radio-inner" />}
                </div>
                <span className="rsvp-option-text">Sitaweza Kuhudhuria</span>
                <FaTimesCircle className="rsvp-option-icon" />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !guestId}
              className="rsvp-submit-btn"
            >
              {loading ? (
                <>
                  <FaSpinner className="fa-spin" /> Inahakiki...
                </>
              ) : (
                'Hakiki'
              )}
            </button>
          </form>
        )}

        {/* Footer info */}
        <div className="rsvp-footer-note">
          Powered by <a href="https://www.harusi.online/" target="_blank" rel="noopener noreferrer">Harusi Online</a>
        </div>
      </div>
    </div>
  );
};

export default PublicRsvp;